import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { toast } from '@src/components/core/toaster';
import {
  listEmailNotifications,
  createEmailNotification,
  updateEmailNotification,
  deleteEmailNotification,
} from '@src/api/actions/emailNotifications';

/**
 * Five Star–style email recipients form: simple Name + Email list, no conditions.
 * All listed recipients receive every lead. Used by both the dashboard
 * settings page and the Five Star onboarding wizard.
 */
export default function EmailRecipientsForm({ clientSlug, helperText }) {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientSlug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    listEmailNotifications(clientSlug)
      .then((res) => setRecipients(res?.data?.results || []))
      .catch(() => setRecipients([]))
      .finally(() => setLoading(false));
  }, [clientSlug]);

  const updateRecipient = (id, patch) => {
    setRecipients((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleAdd = async () => {
    try {
      const res = await createEmailNotification(clientSlug, {
        name: '',
        email: '',
        notify_standard_leads: true,
        notify_self_book_leads: true,
        conditions: [],
      });
      if (res?.data) {
        setRecipients((prev) => [...prev, res.data]);
        toast.success('Recipient added');
      }
    } catch {
      toast.error('Failed to add recipient');
    }
  };

  const handleSave = async (recipient) => {
    try {
      await updateEmailNotification(clientSlug, recipient.id, {
        name: recipient.name,
        email: recipient.email,
        notify_standard_leads: true,
        notify_self_book_leads: true,
        conditions: [],
      });
      toast.success('Saved');
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteEmailNotification(clientSlug, id);
      setRecipients((prev) => prev.filter((r) => r.id !== id));
      toast.success('Recipient removed');
    } catch {
      toast.error('Failed to remove recipient');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {helperText && (
        <Typography variant="body2" color="text.secondary">{helperText}</Typography>
      )}

      {recipients.map((r) => (
        <Card key={r.id} variant="outlined">
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                label="Name"
                size="small"
                value={r.name || ''}
                onChange={(e) => updateRecipient(r.id, { name: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Email"
                size="small"
                type="email"
                value={r.email || ''}
                onChange={(e) => updateRecipient(r.id, { email: e.target.value })}
                sx={{ flex: 1 }}
              />
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => handleSave(r)}>
                  Save
                </Button>
                <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}

      {recipients.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No recipients yet. Add one below.
        </Typography>
      )}

      <Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!clientSlug}
          sx={{ textTransform: 'none' }}
        >
          Add Email Recipient
        </Button>
      </Box>
    </Stack>
  );
}
